import { useMemo } from 'react';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import {
  resolveFlowControlPlayingExpandIdLive,
  resolveFlowControlPlayingExpandIdSimulation,
} from '@/helpers/flow-control-playing-segment.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';

/** Expand id for the segment that is currently playing (simulation or live). */
export function useFlowControlPlayingSegmentExpandId(): string | null {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const flowToUse = flowPlan ?? flow;
  const liveRunning = flowToUse?.liveContext?.isRunning ?? false;

  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationActiveSegmentKind = useFlowControlSimulationCueStore((s) => s.activeSegmentKind);
  const simulationActiveSegmentSlotId = useFlowControlSimulationCueStore((s) => s.activeSegmentSlotId);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);

  const flowId = flowToUse?.origin ? getObjectId(flowToUse.origin) : flowToUse?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flowToUse?.liveContext) : undefined;

  const cueState = useLiveContextCueState(entityRef);

  return useMemo(() => {
    if (liveRunning) {
      return resolveFlowControlPlayingExpandIdLive(cueState);
    }
    return resolveFlowControlPlayingExpandIdSimulation(
      simulationActiveSegmentKind,
      simulationActiveSegmentSlotId,
      simulationSelectedItemIds,
      simulationRows[simulationPointer],
    );
  }, [
    cueState,
    liveRunning,
    simulationActiveSegmentKind,
    simulationActiveSegmentSlotId,
    simulationPointer,
    simulationRows,
    simulationSelectedItemIds,
  ]);
}
